
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const PasswordSection = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<
    { type: 'success' | 'error'; text: string } | null
  >(null);

  const requirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'One number', met: /[0-9]/.test(newPassword) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  const allMet = requirements.every((r) => r.met);
  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword.length > 0;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allMet || !passwordsMatch) return;

    setSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setMessage({
        type: 'success',
        text: 'Password updated successfully!',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || 'Failed to update password.',
      });
    } finally {
      setSaving(false);
    }
  };

  const strengthPercent = requirements.filter((r) => r.met).length * 25;
  const strengthColor =
    strengthPercent <= 25
      ? 'bg-red-400'
      : strengthPercent <= 50
      ? 'bg-orange-400'
      : strengthPercent <= 75
      ? 'bg-yellow-400'
      : 'bg-green-400';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-lock-line text-xl text-[#f04e58]"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
      </div>

      {message && (
        <div
          className={`mb-6 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <i
              className={
                message.type === 'success'
                  ? 'ri-check-line'
                  : 'ri-error-warning-line'
              }
            ></i>
            {message.text}
          </div>
        </div>
      )}

      <form onSubmit={handleChangePassword} className="space-y-5">
        {/* Current Password */}
        <div>
          <label
            htmlFor="currentPw"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Current Password
          </label>
          <div className="relative">
            <input
              id="currentPw"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="Enter current password"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i
                className={showCurrent ? 'ri-eye-off-line' : 'ri-eye-line'}
              ></i>
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label
            htmlFor="newPw"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            New Password
          </label>
          <div className="relative">
            <input
              id="newPw"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className={showNew ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            </button>
          </div>

          {/* Strength bar */}
          {newPassword.length > 0 && (
            <div className="mt-3">
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${strengthColor}`}
                  style={{ width: `${strengthPercent}%` }}
                ></div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {requirements.map((req) => (
                  <li
                    key={req.label}
                    className="flex items-center gap-2 text-xs"
                  >
                    <i
                      className={`${
                        req.met
                          ? 'ri-check-line text-green-500'
                          : 'ri-close-line text-gray-300'
                      }`}
                    ></i>
                    <span
                      className={req.met ? 'text-green-600' : 'text-gray-400'}
                    >
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label
            htmlFor="confirmPw"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Confirm New Password
          </label>
          <div className="relative">
            <input
              id="confirmPw"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i
                className={showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}
              ></i>
            </button>
          </div>
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              Passwords do not match
            </p>
          )}
          {passwordsMatch && (
            <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
              <i className="ri-check-line"></i>
              Passwords match
            </p>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving || !allMet || !passwordsMatch}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Updating...
              </>
            ) : (
              <>
                <i className="ri-lock-line"></i>
                Update Password
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordSection;
